import {
  queryQueueKeys,
  type QueryDetail,
  type QueryLifecycleStatus,
  type QueryListItem,
  type QueryMainStatus,
  type QueryQueueCounts,
  type QueryQueueKey,
  type QueryWorkflowStage,
  type ResourceListResponse,
  type ResourceResponse,
} from '@travel-crm/types';
import type { QueryClient } from '@tanstack/vue-query';

import { apiRequest } from './api-client';
import { assignmentQueryKeys } from './query-assignments';
import { queryWorkspaceKeys } from './query-workspace';

export interface QueryListFilters {
  queue?: QueryQueueKey;
  countryId?: string;
  branchId?: string;
  department?: string;
  ownerId?: string;
  agentId?: string;
  mainStatus?: QueryMainStatus;
  lifecycleStatus?: QueryLifecycleStatus;
  workflowStage?: QueryWorkflowStage;
  reservationOwnerId?: string;
  reservationTeamId?: string;
  salesOwnerId?: string;
  communicationPending?: boolean;
  initialAcknowledgementPending?: boolean;
  travelFrom?: string;
  travelTo?: string;
  createdFrom?: string;
  createdTo?: string;
  cursor?: string;
  limit?: number;
}

export { queryQueueKeys };
export type { QueryQueueCounts, QueryQueueKey };

export const queryListKeys = {
  root: ['query-list'] as const,
  list: (filters: QueryListFilters) => ['query-list', filters] as const,
  detail: (queryId: string) => ['query-detail', queryId] as const,
  queueCounts: ['query-list', 'queue-counts'] as const,
};

/**
 * Invalidates every Query list and the queue-count query together. Both are
 * children of `queryListKeys.root`, so command responses remain the only
 * source of queue movement and no client-side count/list patching is needed.
 */
export function invalidateQueryQueueData(
  queryClient: QueryClient,
): Promise<void> {
  return queryClient.invalidateQueries({ queryKey: queryListKeys.root });
}

export async function invalidateQuerySurfaces(
  queryClient: QueryClient,
  queryId: string,
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: queryWorkspaceKeys.workspace(queryId),
    }),
    queryClient.invalidateQueries({
      queryKey: queryWorkspaceKeys
        .timeline(queryId, undefined, undefined)
        .slice(0, 3),
    }),
    queryClient.invalidateQueries({ queryKey: queryListKeys.detail(queryId) }),
    queryClient.invalidateQueries({
      queryKey: assignmentQueryKeys.history(queryId),
    }),
    invalidateQueryQueueData(queryClient),
  ]);
}

function queryString(filters: QueryListFilters): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  return query.toString();
}

export function fetchQueryList(
  filters: QueryListFilters,
): Promise<ResourceListResponse<QueryListItem>> {
  return apiRequest<ResourceListResponse<QueryListItem>>(
    `/api/v1/queries?${queryString(filters)}`,
  );
}

export function fetchQueryQueueCounts(): Promise<
  ResourceResponse<QueryQueueCounts>
> {
  return apiRequest<ResourceResponse<QueryQueueCounts>>(
    '/api/v1/queries/queue-counts',
  );
}

export async function fetchQueryDetail(queryId: string): Promise<QueryDetail> {
  return (
    await apiRequest<ResourceResponse<QueryDetail>>(
      `/api/v1/queries/${queryId}`,
    )
  ).data;
}

export async function markQueryLost(input: {
  queryId: string;
  expectedVersion: number;
  reason: string;
}): Promise<QueryDetail> {
  return (
    await apiRequest<ResourceResponse<QueryDetail>>(
      `/api/v1/queries/${input.queryId}/lost`,
      {
        method: 'POST',
        body: JSON.stringify({
          expectedVersion: input.expectedVersion,
          reason: input.reason,
        }),
      },
    )
  ).data;
}

export async function reopenColdQuery(input: {
  queryId: string;
  expectedVersion: number;
  travelStartDate: string;
  travelEndDate: string;
  reason: string;
}): Promise<QueryDetail> {
  return (
    await apiRequest<ResourceResponse<QueryDetail>>(
      `/api/v1/queries/${input.queryId}/reopen`,
      {
        method: 'POST',
        body: JSON.stringify({
          expectedVersion: input.expectedVersion,
          travelStartDate: input.travelStartDate,
          travelEndDate: input.travelEndDate,
          reason: input.reason,
        }),
      },
    )
  ).data;
}
